import React, { useState, useEffect, useContext } from "react";
import cookie from "js-cookie";
import { Product } from "src/interfaces/sanity";
import { eventData } from "src/utils/dataLayer";
import gaEvent from "src/utils/gaEvent";

const SHOPIFY_CART_STORAGE_KEY = "shopify_cart_id";

async function shopifyFetch({ query, variables }: { query: string; variables?: any }) {
  const endpoint = `https://${process.env.GATSBY_SHOPIFY_STORE}/api/2023-10/graphql.json`;
  
  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": process.env.GATSBY_SHOPIFY_STOREFRONT_TOKEN || "",
    },
    body: JSON.stringify({ query, variables }),
  });

  return response.json();
}

const CartFragment = `
  fragment CartFragment on Cart {
    id
    checkoutUrl
    totalQuantity
    cost {
      totalAmount {
        amount
        currencyCode
      }
      subtotalAmount {
        amount
        currencyCode
      }
    }
    lines(first: 100) {
      edges {
        node {
          id
          quantity
          merchandise {
            ... on ProductVariant {
              id
              title
              price {
                amount
                currencyCode
              }
              product {
                id
                title
                handle
              }
              image {
                url
                altText
              }
            }
          }
          attributes {
            key
            value
          }
          discountAllocations {
            discountedAmount {
              amount
              currencyCode
            }
            discountApplication {
              value {
                ... on PricingPercentageValue {
                  percentage
                }
                ... on MoneyV2 {
                  amount
                  currencyCode
                }
              }
            }
          }
        }
      }
    }

    discountCodes {
      code
      applicable
    }

    discountAllocations {
      discountedAmount {
        amount
        currencyCode
      }
      discountApplication {
        targetType
        allocationMethod
        value {
          ... on PricingPercentageValue {
            percentage
          }
          ... on MoneyV2 {
            amount
            currencyCode
          }
        }
      }
    }
  }
`;

const CREATE_CART_MUTATION = `
  mutation createCart {
    cartCreate {
      cart {
        ...CartFragment
      }
    }
  }
  ${CartFragment}
`;

const GET_CART_QUERY = `
  query getCart($cartId: ID!) {
    cart(id: $cartId) {
      ...CartFragment
    }
  }
  ${CartFragment}
`;

const ADD_LINES_MUTATION = `
  mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        ...CartFragment
      }
      userErrors {
        field
        message
      }
    }
  }
  ${CartFragment}
`;

const REMOVE_LINES_MUTATION = `
  mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart {
        ...CartFragment
      }
      userErrors {
        field
        message
      }
    }
  }
  ${CartFragment}
`;

const UPDATE_LINES_MUTATION = `
  mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart {
        ...CartFragment
      }
      userErrors {
        field
        message
      }
    }
  }
  ${CartFragment}
`;

const UPDATE_DISCOUNT_MUTATION = `
  mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]) {
    cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
      cart {
        ...CartFragment
      }
      userErrors {
        field
        message
      }
    }
  }
  ${CartFragment}
`;

const UPDATE_ATTRIBUTES_MUTATION = `
  mutation cartAttributesUpdate($cartId: ID!, $attributes: [AttributeInput!]!) {
    cartAttributesUpdate(cartId: $cartId, attributes: $attributes) {
      cart {
        ...CartFragment
      }
      userErrors {
        field
        message
      }
    }
  }
  ${CartFragment}
`;

// Types
interface Cart {
  id: string;
  checkoutUrl: string;
  totalQuantity: number;
  cost: {
    totalAmount: { amount: string; currencyCode: string };
    subtotalAmount: { amount: string; currencyCode: string };
  };
  lines: {
    edges: Array<{
      node: {
        id: string;
        quantity: number;
        merchandise: any;
        attributes: any[];
        discountAllocations: any[];
      };
    }>;
  };
  discountCodes: Array<{
    code: string;
    applicable: boolean;
  }>;
}

interface InitialStore {
  isAdding: boolean;
  cartIsOpen: boolean;
  drawerOpen: boolean;
  drawerType:
  | "search"
  | "reviews"
  | "cart"
  | "additionalInformation"
  | undefined;
  reviewsType: "questions" | undefined;
  productInfo: {};
  navIsOpen: boolean;
  page: undefined;
  orders: any[];
  customerEmail: string | undefined;
  customerName: string | undefined;
  customerToken: string | undefined;
  cart: Cart;
  mobileMenuOpen: boolean;
  pillowcaseId: string | null;
  upsells: {
    open: boolean;
    boughtItem: {
      title: string;
      quantity: any;
      price: string;
      imageRef: string;
    } | null;
    productList: Array<{
      _type: "upsellProduct";
      _key: string;
      price: number;
      product: Product;
      type: "add" | "upgrade";
    }>;
  };
}

const initialStoreState: InitialStore = {
  isAdding: false,
  cartIsOpen: false,
  drawerOpen: false,
  drawerType: undefined,
  reviewsType: undefined,
  productInfo: {},
  page: undefined,
  customerEmail: undefined,
  customerName: undefined,
  customerToken: undefined,
  orders: [],
  navIsOpen: false,
  pillowcaseId: null,
  cart: {
    id: "",
    checkoutUrl: "",
    totalQuantity: 0,
    cost: {
      totalAmount: { amount: "0.00", currencyCode: "USD" },
      subtotalAmount: { amount: "0.00", currencyCode: "USD" },
    },
    lines: { edges: [] },
    discountCodes: [],
  },
  mobileMenuOpen: false,
  upsells: {
    open: false,
    boughtItem: null,
    productList: [],
  },
};

async function attachAwinAttributesToCart(cartId) {
  if (typeof window === "undefined" || !cartId) return;

	const cookieName = process.env.GATSBY_SANITY_DATASET === "production" ? "_awc_120706=" : "_awc_123974="
  
  try {
    const awcCookie = document.cookie
      .split("; ")
      .find((row) => row.startsWith(cookieName))
      ?.split("=")[1];
    const awChannel = document.cookie
      .split("; ")
      .find((row) => row.startsWith("_aw_channel="))
      ?.split("=")[1];

    if (!awcCookie && !awChannel) return;

    const attributes = [];
    if (awcCookie) attributes.push({ key: "__awc", value: awcCookie });
    if (awChannel) attributes.push({ key: "__awin_channel", value: awChannel });

    await shopifyFetch({
      query: UPDATE_ATTRIBUTES_MUTATION,
      variables: {
        cartId,
        attributes,
      },
    });
  } catch (err) {
    console.warn("[AWIN] Failed to attach tracking attributes:", err);
  }
}


export const StoreContext = React.createContext({
  store: initialStoreState,
  setStore: (store: any) => null,
});

const createNewCart = async (): Promise<Cart> => {
  const { data, errors } = await shopifyFetch({
    query: CREATE_CART_MUTATION,
  });

  if (errors) {
    console.error("Error creating cart", errors);
    return initialStoreState.cart;
  }

  return data.cartCreate.cart;
};

const fetchCart = async (id: string): Promise<Cart | null> => {
  const { data, errors } = await shopifyFetch({
    query: GET_CART_QUERY,
    variables: { cartId: id },
  });

  if (errors || !data?.cart) {
    return null;
  }

  return data.cart;
};

const setCartInState = (cart: Cart, setStore: any) => {
  const isBrowser = typeof window !== "undefined";
  if (isBrowser && cart?.id) {
    localStorage.setItem(SHOPIFY_CART_STORAGE_KEY, cart.id);
  }

  setStore((prevState: InitialStore) => {
    return { ...prevState, cart };
  });
};

const initCustomer = (setStore: any) => {
  const customerEmail = cookie.get("customer_email");
  const customerToken = cookie.get("customer_token");
  const customerName = cookie.get("customer_firstName");

  if (customerEmail && customerToken && customerName) {
    setStore((prevState: InitialStore) => {
      return { ...prevState, customerEmail, customerToken, customerName };
    });
  }
};

const StoreContextProvider = ({ children }: { children: any }) => {
  const [store, setStore] = useState(initialStoreState);
  const [initStore, setInitStore] = useState(false);

  useEffect(() => {
    if (initStore === false) {
      const initializeCart = async () => {
        const isBrowser = typeof window !== "undefined";
        const existingCartId = isBrowser
          ? localStorage.getItem(SHOPIFY_CART_STORAGE_KEY)
          : null;

        if (existingCartId) {
          try {
            const cart = await fetchCart(existingCartId);
            
            if (cart) {
              setCartInState(cart, setStore);
              attachAwinAttributesToCart(cart.id);
              return;
            }
          } catch (e) {
            console.error("Error fetching existing cart, creating new one.", e);
            localStorage.removeItem(SHOPIFY_CART_STORAGE_KEY);
          }
        }

        const newCart = await createNewCart();
        setCartInState(newCart, setStore);
      };
      initCustomer(setStore);
      initializeCart();
      setInitStore(true);
    }
  }, [initStore]);

  return (
    <StoreContext.Provider
      value={{
        store,
        setStore,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
};

function useStore() {
  const { store } = useContext(StoreContext);
  return store;
}

function useCartCount() {
  const {
    store: { cart },
  } = useContext(StoreContext);

  return cart?.totalQuantity || 0;
}

const setCustomerInState = () => {
  const { setStore }: { setStore: any } = useContext(StoreContext);

  async function updateCustomerInState() {
    const customerEmail = cookie.get("customer_email");
    const customerToken = cookie.get("customer_token");
    const customerName = cookie.get("customer_firstName");
    setStore((prevState: InitialStore) => {
      return { ...prevState, customerEmail, customerToken, customerName };
    });
  }

  return updateCustomerInState;
};

function useDiscount() {
  const {
    store: { cart },
    setStore,
  }: { store: InitialStore; setStore: any } = useContext(StoreContext);

  async function removeDiscount() {
    // To remove discounts in Cart API, we update with an empty array
    const { data, errors } = await shopifyFetch({
      query: UPDATE_DISCOUNT_MUTATION,
      variables: {
        cartId: cart.id,
        discountCodes: []
      }
    });

    if (errors || data?.cartDiscountCodesUpdate?.userErrors?.length > 0) {
      console.error("Failed to remove discount", errors || data?.cartDiscountCodesUpdate?.userErrors);
      return;
    }

    setStore((prevState: InitialStore) => {
      return { ...prevState, cart: data.cartDiscountCodesUpdate.cart };
    });
  }

  async function addDiscount(discountCode: string) {
    const { data, errors } = await shopifyFetch({
      query: UPDATE_DISCOUNT_MUTATION,
      variables: {
        cartId: cart.id,
        discountCodes: [discountCode]
      }
    });

    if (errors || data?.cartDiscountCodesUpdate?.userErrors?.length > 0) {
      console.error("Failed to add discount", errors || data?.cartDiscountCodesUpdate?.userErrors);
      return;
    }

    setStore((prevState: InitialStore) => {
      return { ...prevState, cart: data.cartDiscountCodesUpdate.cart };
    });
  }

  return { addDiscount, removeDiscount };
}


function useCheckoutId() {
  const {
    store: { cart },
  } = useContext(StoreContext);

  return cart?.id;
}

function useCart() {
  const {
    store: { cart },
  } = useContext(StoreContext);

  if (cart?.id) {
    const total = cart.cost?.totalAmount
    ? parseFloat(cart.cost.totalAmount.amount)
    : 0;

    const discountCodes = cart.discountCodes || [];
    const hasDiscounts = discountCodes.length > 0;
    const discount = hasDiscounts ? discountCodes[0] : null;

    let discountPercentage = 0;
    let discountedAmount = 0;

    if (cart.discountAllocations && cart.discountAllocations.length > 0) {
      cart.discountAllocations.forEach((allocation) => {
        const amount = parseFloat(allocation.discountedAmount.amount);
        if (!isNaN(amount)) {
          discountedAmount += amount;
        }

        const pct = allocation.discountApplication?.value?.percentage;
        if (pct) {
          discountPercentage = pct;
        }
      });
    }

    const mappedLineItems = cart.lines.edges.map((edge) => {
      const node = edge.node;

      const itemDiscount = node.discountAllocations.reduce((acc, d) => {
        const amount = parseFloat(d.discountedAmount.amount);
        return acc + (isNaN(amount) ? 0 : amount);
      }, 0);

      discountedAmount += itemDiscount;

      if (discountPercentage === 0) {
        const lineLevelPercent = node.discountAllocations.find(
          (d) => d.discountApplication?.value?.percentage
        )?.discountApplication?.value?.percentage;

        if (lineLevelPercent) {
          discountPercentage = lineLevelPercent;
        }
      }

      return {
        ...node,
        title: node.merchandise?.product?.title || node.merchandise?.title,
        variant: node.merchandise,
      };
    });


    return {
      checkout: {
        id: cart.id,
        webUrl: cart.checkoutUrl,
        lineItems: mappedLineItems,
        totalPriceV2: cart.cost.totalAmount,
        subtotalPriceV2: cart.cost.subtotalAmount,
        discount: {
          code: discount?.code,
          percentage: discountPercentage,
          applicable: discountedAmount > 0 || hasDiscounts,
          amount: parseFloat(discountedAmount.toFixed(2)),
          has: hasDiscounts || discountedAmount > 0, 
        },
        freeShipping: false,
        total
      },
    };
  }

  return {
    checkout: {
      lineItems: [],
      discount: {
        percentage: 0,
        amount: 0,
        has: false,
      },
      total: "0.00",
    },
  };
}

function useCustomer() {
  const {
    store: { customerEmail, customerName, customerToken },
  } = useContext(StoreContext);

  return { customerEmail, customerName, customerToken };
}

function useAddItemToCart() {
  const {
    store: { cart },
    setStore,
  }: { store: InitialStore; setStore: any } = useContext(StoreContext);
  const { addDiscount } = useDiscount();

  // IDs kept for reference based on original logic
  const freeGiftVariantId = "gid://shopify/ProductVariant/32876393955414";
  const freeEcourseVariantId = "gid://shopify/ProductVariant/40074074456150";
  const freeGiftVariantIdUS = "gid://shopify/ProductVariant/42756017750166";

  async function addItemToCart(
    variantId: string,
    quantity: number,
    attributes?: { key: string; value: string }[],
    skipCart?: boolean
  ) {
    if (variantId === "" || !quantity) {
      console.error("Both a size and quantity are required.");
      return;
    }

    setStore((prevState: InitialStore) => {
      return { ...prevState, isAdding: true };
    });

    const lines = [{
      merchandiseId: variantId,
      quantity,
      attributes: attributes || []
    }];

    const { data, errors } = await shopifyFetch({
      query: ADD_LINES_MUTATION,
      variables: {
        cartId: cart.id,
        lines,
      }
    });

    if(errors || data?.cartLinesAdd?.userErrors?.length > 0) {
      console.error("Error adding to cart", errors || data?.cartLinesAdd?.userErrors);
      setStore((prevState: InitialStore) => ({ ...prevState, isAdding: false }));
      return;
    }

    const newCart = data.cartLinesAdd.cart;

    setStore((prevState: InitialStore) => {
      return {
        ...prevState,
        cart: newCart,
        drawerOpen: skipCart ? false : true,
        drawerType: skipCart ? undefined : "cart",
        isAdding: false,
      };
    });

    // Re-apply stored discount code if exists in localStorage (legacy behavior)
    const discountCode = window.localStorage.getItem("applyDiscount");
    if (discountCode) await addDiscount(discountCode);
  }

  async function addItemsToCart(list: any[]) {
     const lines = list.map((item) => {
       if (item.variantId === "" || !item.quantity) {
         console.error("Both a size and quantity are required.");
         return null;
       }
       return {
         merchandiseId: item.variantId,
         quantity: item.quantity,
         attributes: item.attributes || [],
       };
     }).filter(Boolean);

     if(lines.length === 0) return;

     setStore((prevState: InitialStore) => {
       return { ...prevState, isAdding: true };
     });

     const { data, errors } = await shopifyFetch({
       query: ADD_LINES_MUTATION,
       variables: {
         cartId: cart.id,
         lines,
       }
     });

    if(errors || data?.cartLinesAdd?.userErrors?.length > 0) {
      console.error("Error adding items to cart", errors || data?.cartLinesAdd?.userErrors);
      setStore((prevState: InitialStore) => ({ ...prevState, isAdding: false }));
      return;
    }

    const newCart = data.cartLinesAdd.cart;
     
     setStore((prevState: InitialStore) => {
      return { 
        ...prevState, 
        cart: newCart,
        drawerOpen: true,
        drawerType: "cart",
        isAdding: false 
      };
    });
  }

  return { addItemToCart, addItemsToCart };
}

function addToCartRedirectToCheckout() {
  const {
    store: { cart },
    setStore,
  }: { store: InitialStore; setStore: any } = useContext(StoreContext);

  async function addItemToCart(
    variantId: string,
    quantity: number,
    attributes?: { key: string; value: string }[],
    skipCart?: boolean
  ) {
    if (variantId === "" || !quantity) {
      console.error("Both a size and quantity are required.");
      return;
    }

    setStore((prevState: InitialStore) => {
      return { ...prevState, isAdding: true };
    });

    const lines = [{
      merchandiseId: variantId,
      quantity,
      attributes: attributes || []
    }];

    const { data, errors } = await shopifyFetch({
      query: ADD_LINES_MUTATION,
      variables: {
        cartId: cart.id,
        lines,
      }
    });

    if(errors || data?.cartLinesAdd?.userErrors?.length > 0) {
      console.error("Error adding to cart (redirect)", errors || data?.cartLinesAdd?.userErrors);
      setStore((prevState: InitialStore) => ({ ...prevState, isAdding: false }));
      return;
    }

    const newCart = data.cartLinesAdd.cart;

    setStore((prevState: InitialStore) => {
      return {
        ...prevState,
        cart: newCart,
        drawerOpen: skipCart ? false : true,
        drawerType: skipCart ? undefined : "test",
        isAdding: false,
      };
    });
  }

  async function addItemsToCart(list: any[]) {
    const lines = list.map((item) => {
      return {
        merchandiseId: item.variantId,
        quantity: item.quantity,
        attributes: item.attributes || [],
      };
    });

    setStore((prevState: InitialStore) => {
      return { ...prevState, isAdding: true };
    });

    const { data, errors } = await shopifyFetch({
      query: ADD_LINES_MUTATION,
      variables: {
        cartId: cart.id,
        lines,
      }
    });

    if(errors || data?.cartLinesAdd?.userErrors?.length > 0) {
      console.error("Error adding items to cart (redirect)", errors || data?.cartLinesAdd?.userErrors);
      setStore((prevState: InitialStore) => ({ ...prevState, isAdding: false }));
      return;
    }

    const newCart = data.cartLinesAdd.cart;

    setStore((prevState: InitialStore) => {
      return {
        ...prevState,
        cart: newCart,
        drawerOpen: false,
        drawerType: false,
        isAdding: false,
      };
    });
  }

  return () => {
    function redirectCheckoutAfterItems() {
      if(!cart.checkoutUrl) return;
      
      return new Promise((resolve) => {
        setTimeout(() => {
          if (
            typeof window.ga === "undefined" ||
            window.ga === null ||
            typeof window.ga.getAll === "undefined"
          ) {
            window.open(cart.checkoutUrl, "_self");
            return;
          }

          const linkerParam = window.ga.getAll()[0].get("linkerParam");
          window.open(`${cart.checkoutUrl}&${linkerParam}`, "_self");
        }, 1000);
      });
    }

    async function redirectToCheckout() {
      const result = await redirectCheckoutAfterItems();
    }

    redirectToCheckout();
    if (window.google_tag_manager) {
      window.dataLayer = window.dataLayer || [];
      dataLayer?.push({
        ...eventData("dl_begin_checkout"),
        eventTimeout: 2000,
        cart_total: cart.cost?.totalAmount?.amount,
        ecommerce: {
          currencyCode:
            process.env.GATSBY_CURRENCY_SYMBOL == "$" ? "USD" : "GBP",
          checkout: {
            products: [],
          },
        },
        eventCallback: redirectToCheckout,
      });
    } else {
      redirectToCheckout();
    }
  };
}

function useRemoveItemFromCart() {
  const {
    store: { cart },
    setStore,
  }: {
    store: InitialStore;
    setStore: any;
  } = useContext(StoreContext);

  async function removeItemFromCart(itemId) {
    const { data, errors } = await shopifyFetch({
      query: REMOVE_LINES_MUTATION,
      variables: {
        cartId: cart.id,
        lineIds: [itemId]
      }
    });

    if(errors || data?.cartLinesRemove?.userErrors?.length > 0) {
      console.error("Error removing item", errors || data?.cartLinesRemove?.userErrors);
      return;
    }

    setStore((prevState: InitialStore) => {
      return { ...prevState, cart: data.cartLinesRemove.cart };
    });
  }

  return removeItemFromCart;
}

function useUpdateItemsFromCart() {
  const {
    store: { cart },
    setStore,
  }: {
    store: InitialStore;
    setStore: any;
  } = useContext(StoreContext);

  async function updateItemsFromCart(items: any) {
    const itemsArray = [].concat(items);
    
    // Map to Cart API input structure
    const lines = itemsArray.map(item => ({
      id: item.id,
      quantity: item.quantity,
      attributes: item.attributes // Optional if attributes are being updated too
    }));
  
    // Optimistic update logic preserved from original file logic, 
    // but mapped to the new cart structure (edges/node) in state
    setStore((prevState: InitialStore) => {
       // Deep copy needed for safe mutation of nested state
       const newEdges = prevState.cart.lines.edges.map(edge => {
         const matchingUpdate = lines.find(l => l.id === edge.node.id);
         if(matchingUpdate) {
           return {
             ...edge,
             node: {
               ...edge.node,
               quantity: matchingUpdate.quantity
             }
           }
         }
         return edge;
       });

       return {
         ...prevState,
         cart: {
           ...prevState.cart,
           lines: {
             ...prevState.cart.lines,
             edges: newEdges
           }
         }
       }
    });
  
    try {
      const { data, errors } = await shopifyFetch({
        query: UPDATE_LINES_MUTATION,
        variables: {
          cartId: cart.id,
          lines
        }
      });

      if(errors || data?.cartLinesUpdate?.userErrors?.length > 0) {
         console.error("Error updating lines", errors || data?.cartLinesUpdate?.userErrors);
         // Ideally revert state here if needed, but for now we log error
         return;
      }

      setStore((prevState: InitialStore) => {
        return { ...prevState, cart: data.cartLinesUpdate.cart };
      });
    } catch (error) {
      console.error("Failed to update checkout", error);
    }
  }
  

  return updateItemsFromCart;
}

function useCheckout() {
  const {
    store: { cart },
  }: {
    store: InitialStore;
  } = useContext(StoreContext);

  return () => {
    const redirectToCheckout = () => {
      if(!cart.checkoutUrl) return;

      if (
        typeof window.ga === "undefined" ||
        window.ga === null ||
        typeof window.ga.getAll === "undefined"
      ) {
        window.open(cart.checkoutUrl, "_self");
        return;
      }

      const linkerParam = window.ga.getAll()[0].get("linkerParam");
      let webUrl = cart.checkoutUrl;
      
      // Basic domain swap logic if needed
      if(process.env.GATSBY_CHECKOUT_DOMAIN) {
         try {
           webUrl = webUrl.replace(new URL(cart.checkoutUrl).hostname, process.env.GATSBY_CHECKOUT_DOMAIN);
         } catch(e) { console.error("Error replacing checkout domain", e); }
      }
      
      webUrl = webUrl.replace(/channel=[^&]*/, "");
      window.location.href = `${webUrl}${linkerParam ? `&${linkerParam}` : ""}`;
    };

    if (window.google_tag_manager) {
      window.dataLayer = window.dataLayer || [];
      dataLayer?.push({
        ...eventData("dl_begin_checkout"),
        eventTimeout: 2000,
        cart_total: cart.cost?.totalAmount?.amount,
        ecommerce: {
          currencyCode:
            process.env.GATSBY_CURRENCY_SYMBOL == "$" ? "USD" : "GBP",
          checkout: {
            products: [],
          },
        },
        eventCallback: redirectToCheckout,
      });
    } else {
      redirectToCheckout();
    }
  };
}

function useSetPage() {
  const {
    setStore,
  }: {
    setStore: any;
  } = useContext(StoreContext);
  async function setPage(page: string) {
    setStore((prevState: InitialStore) => {
      return { ...prevState, page };
    });
  }
  return setPage;
}

function useToggleCart() {
  const {
    store: { cartIsOpen },
    setStore,
  }: {
    store: InitialStore;
    setStore: any;
  } = useContext(StoreContext);

  async function toggleCart() {
    setStore((prevState: InitialStore) => {
      return { ...prevState, cartIsOpen: !cartIsOpen };
    });
  }

  return toggleCart;
}

function useDrawer() {
  const {
    store: { drawerOpen },
    setStore,
  }: {
    store: InitialStore;
    setStore: any;
  } = useContext(StoreContext);

  async function toggleDrawer() {
    setStore((prevState: InitialStore) => {
      return { ...prevState, drawerOpen: !drawerOpen };
    });
  }

  return toggleDrawer;
}

function closeDrawer() {
  const {
    setStore,
  }: {
    setStore: any;
  } = useContext(StoreContext);

  return async function toggleDrawer() {
    setStore((prevState: InitialStore) => {
      return { ...prevState, drawerOpen: false, productInfo: {} };
    });
  };
}

const useReviewDrawer = () => {
  const { setStore }: { setStore: any } = useContext(StoreContext);

  const openReviews = async (product: any) => {
    setStore((prevState: InitialStore) => {
      return {
        ...prevState,
        drawerOpen: true,
        drawerType: "reviews",
        productInfo: product,
        reviewsType: product.type,
      };
    });
  };

  const closeReviews = async () => {
    setStore((prevState: InitialStore) => {
      return {
        ...prevState,
        drawerOpen: false,
        drawerType: undefined,
        reviewsType: undefined,
        productInfo: {},
      };
    });
  };

  return { openReviews, closeReviews };
};

const useReturnMoneyDrawer = () => {
  const { setStore }: { setStore: any } = useContext(StoreContext);

  const openReturnMoney = async (product: any) => {
    setStore((prevState: InitialStore) => {
      return {
        ...prevState,
        drawerOpen: true,
        drawerType: "returnMoney",
      };
    });
  };

  const closeReturnMoney = async () => {
    setStore((prevState: InitialStore) => {
      return {
        ...prevState,
        drawerOpen: false,
        drawerType: undefined,
        productInfo: {},
      };
    });
  };

  return { openReturnMoney, closeReturnMoney };
};

const useSearchDrawer = () => {
  const { setStore }: { setStore: any } = useContext(StoreContext);

  const openSearch = async () => {
    setStore((prevState: InitialStore) => {
      return { ...prevState, drawerOpen: true, drawerType: "search" };
    });
  };

  const closeSearch = async () => {
    setStore((prevState: InitialStore) => {
      return { ...prevState, drawerOpen: false, drawerType: undefined };
    });
  };

  return { openSearch, closeSearch };
};

const useCartDrawer = () => {
  const { setStore }: { setStore: any } = useContext(StoreContext);

  const openCart = async () => {
    setStore((prevState: InitialStore) => {
      return { ...prevState, drawerOpen: true, drawerType: "cart" };
    });
  };

  const closeCart = async () => {
    setStore((prevState: InitialStore) => {
      return { ...prevState, drawerOpen: false, drawerType: undefined };
    });
  };

  return { openCart, closeCart };
};

const useAdditionalInformationDrawer = () => {
  const { setStore }: { setStore: any } = useContext(StoreContext);

  const openAdditionalInformation = async () => {
    gaEvent("See medical evidence", "Click", "Medical");

    setStore((prevState: InitialStore) => {
      return {
        ...prevState,
        drawerOpen: true,
        drawerType: "additionalInformation",
      };
    });
  };

  const closeAdditionalInformation = async () => {
    setStore((prevState: InitialStore) => {
      return { ...prevState, drawerOpen: false, drawerType: undefined };
    });
  };

  return { openAdditionalInformation, closeAdditionalInformation };
};

const usePillowcaseMaterialDrawer = () => {
  const { setStore }: { setStore: any } = useContext(StoreContext);

  const openPillowcaseMaterial = async (id: string) => {
    setStore((prevState: InitialStore) => {
      return {
        ...prevState,
        drawerOpen: true,
        drawerType: "pillowcaseMaterial",
        pillowcaseId: id,
      };
    });
  };

  const closePillowcaseMaterial = async () => {
    setStore((prevState: InitialStore) => {
      return {
        ...prevState,
        drawerOpen: false,
        drawerType: undefined,
        pillowcaseId: null,
      };
    });
  };

  return { openPillowcaseMaterial, closePillowcaseMaterial };
};

const useMobileMenu = () => {
  const { setStore }: { setStore: any } = useContext(StoreContext);

  const openMenu = async () => {
    setStore((prevState: InitialStore) => {
      return {
        ...prevState,
        mobileMenuOpen: true,
      };
    });
  };

  const closeMenu = async () => {
    setStore((prevState: InitialStore) => {
      return {
        ...prevState,
        mobileMenuOpen: false,
      };
    });
  };

  return { openMenu, closeMenu };
};

const useUpsells = () => {
  const { setStore }: { setStore: any } = useContext(StoreContext);

  const openUpsells = async ({
    boughtItem,
    productList,
  }: {
    boughtItem: {
      id: string;
      title: string;
      quantity: any;
      price: string;
      imageRef: string;
    };
    productList: Array<{
      _type: "upsellProduct";
      _key: string;
      price: number;
      product: Product;
      type: "add" | "upgrade";
    }>;
  }) => {
    setStore((prevState: InitialStore) => {
      return {
        ...prevState,
        upsells: {
          open: true,
          boughtItem,
          productList,
        },
      };
    });
  };

  const closeUpsells = async () => {
    setStore((prevState: InitialStore) => {
      return {
        ...prevState,
        upsells: {
          open: false,
          boughtItem: null,
          productList: [],
        },
      };
    });
  };

  return { openUpsells, closeUpsells };
};

export {
  StoreContextProvider,
  setCustomerInState,
  useAddItemToCart,
  useCheckoutId,
  useDiscount,
  useStore,
  useCustomer,
  useCartCount,
  useCart,
  useSetPage,
  useRemoveItemFromCart,
  useUpdateItemsFromCart,
  useCheckout,
  useToggleCart,
  useDrawer,
  useReviewDrawer,
  useReturnMoneyDrawer,
  useSearchDrawer,
  useCartDrawer,
  useAdditionalInformationDrawer,
  usePillowcaseMaterialDrawer,
  useMobileMenu,
  useUpsells,
  addToCartRedirectToCheckout,
};